import { MailLanguage } from "../../emailLanguage";
import { ApiResult, postJson } from "../../restClient";

/**
 * The login-free cancellation endpoints - contract with
 * `backend/GrooveGrid.Api/Controllers/CancellationController.cs`:
 *
 *   POST /api/cancellation/preview   -> CancellationPreview
 *   POST /api/cancellation/confirm   -> CancellationReceipt   (rate limited)
 *
 * Both unauthenticated and neither behind a token - a token is the hurdle
 * § 312k abolished.
 *
 * ⚠️ Neither endpoint ever says whether an address belongs to a customer, so the
 * form cannot be used to find out who has an account. Only the confirmation mail
 * carries an answer about the contract, which is why this page never renders plan
 * data or dates.
 */

/** What the person typed on the confirmation page. */
export interface CancellationFormValues {
  /** § 312k Abs. 2 Satz 3 Nr. 3 - the details that identify the customer. */
  name: string;
  email: string;
  /**
   * § 312k Abs. 2 Satz 3 Nr. 2 - the designation of the contract. Optional:
   * demanding a designation only we know would be a hurdle before the button.
   */
  contractReference: string;
  /** § 312k Abs. 2 Satz 3 Nr. 1 - the kind of cancellation. */
  kind: CancellationKind;
  /** Required for an extraordinary cancellation, empty otherwise. */
  extraordinaryReason: string;
  /** § 312k Abs. 2 Satz 3 Nr. 4 - when the cancellation is to take effect. */
  timing: CancellationTiming;
  /** ISO date (yyyy-mm-dd) when `timing` is "specificDate", otherwise empty. */
  desiredEffectiveOn: string;
  /** Anything else the person wanted to say. Free form, the declaration is too. */
  note: string;
}

/** Ordinary cancellation under AGB 16/17, or extraordinary for cause. */
export type CancellationKind = "ordinary" | "extraordinary";

/** To the next possible date, or to a date the customer names. */
export type CancellationTiming = "nextPossible" | "specificDate";

/**
 * The body both endpoints take.
 *
 * ⚠️ `kind` and `desiredEffectiveOn` are read, not decoration: they land in their
 * own columns and the backend acts on them, because an extraordinary declaration
 * or a customer-named date must not be confirmed with the AGB 16/17 period end.
 *
 * `extraordinaryReason` has no column yet, so the page folds it into `note`,
 * which is recorded verbatim. Do not drop it from the note before it gets one.
 */
export interface CancellationSubmissionBody {
  name: string;
  email: string;
  contractReference: string;
  /** The free text plus the folded-in reason for an extraordinary cancellation. */
  note: string;
  /** "de" or "en" - picks the template the confirmation is written in. */
  language: MailLanguage;
  /** § 312k Abs. 2 Satz 3 Nr. 1, read into `CancellationRequest.DeclaredKind`. */
  kind: CancellationKind;
  /** Kept in `note`; see above. Sent as its own field for the day it gets a column. */
  extraordinaryReason: string;
  /**
   * Which timing option was chosen. Redundant on the wire, sent anyway so the
   * record shows what the customer was asked, not only what he answered.
   */
  timing: CancellationTiming;
  /**
   * § 312k Abs. 2 Satz 3 Nr. 4 as `yyyy-mm-dd`, read into
   * `CancellationRequest.DesiredEffectiveOn`. Empty for "next possible date".
   */
  desiredEffectiveOn: string;
}

/** The confirmation page of § 312k Abs. 2, as the backend hands it back. */
export interface CancellationPreview {
  /** The four values as they will be recorded - trimmed, otherwise untouched. */
  name: string | null;
  email: string | null;
  contractReference: string | null;
  note: string | null;
  /** "de" or "en" - the language the confirmation will be written in. */
  language: string;
  /** Always true - the page has to say that the button is the cancellation. */
  effectiveOnSubmit: boolean;
  /**
   * Key of the sentence about when the contract ends; the backend has no
   * localisation, so it names a rule and this app writes it out.
   */
  contractEndRule: string;
  /** Where the confirmation posts to, for documentation. */
  confirmPath: string;
  /** The address a cancellation can always be sent to instead (AGB 17). */
  contactEmail: string;
}

/** The receipt of a submitted cancellation. */
export interface CancellationReceipt {
  /** Reference of the recorded declaration, quoted back to the customer. */
  requestId: string;
  /**
   * When the declaration was received and took effect. ⚠️ Not the end of the
   * contract - that follows AGB 16/17 and is named only in the confirmation mail.
   */
  receivedAt: string;
}

/**
 * Names of `CancellationInputProblem` that point at a single field, mapped onto
 * that field. Anything else is shown as a general failure.
 */
export const PROBLEM_FIELDS: Record<string, keyof CancellationFormValues> = {
  NameMissing: "name",
  NameTooLong: "name",
  EmailMissing: "email",
  EmailMalformed: "email",
  EmailTooLong: "email",
  ContractReferenceTooLong: "contractReference",
  NoteTooLong: "note",
};

const toBody = (
  values: CancellationFormValues,
  note: string,
  language: MailLanguage,
): CancellationSubmissionBody => ({
  name: values.name.trim(),
  email: values.email.trim(),
  contractReference: values.contractReference.trim(),
  note,
  language,
  kind: values.kind,
  extraordinaryReason:
    values.kind === "extraordinary" ? values.extraordinaryReason.trim() : "",
  timing: values.timing,
  desiredEffectiveOn:
    values.timing === "specificDate" ? values.desiredEffectiveOn : "",
});

/**
 * Builds the confirmation page. Hands back only what was typed, so the customer
 * confirms the exact wording that will be recorded. Never throws.
 */
export const previewCancellation = (
  values: CancellationFormValues,
  note: string,
  language: MailLanguage,
): Promise<ApiResult<CancellationPreview>> =>
  postJson<CancellationPreview>(
    "cancellation/preview",
    toBody(values, note, language),
  );

/**
 * Submits the cancellation. This IS the cancellation - effective the moment it
 * returns, with no second step behind a mail link. Never throws.
 */
export const confirmCancellation = (
  values: CancellationFormValues,
  note: string,
  language: MailLanguage,
): Promise<ApiResult<CancellationReceipt>> =>
  postJson<CancellationReceipt>(
    "cancellation/confirm",
    toBody(values, note, language),
  );
